import { TeamWith } from "@redotech/account-common/base-team";
import { NonZeroValueExchangeStrategy } from "@redotech/redo-common-model/exchange";
import type { StagecoachInferenceMode } from "@redotech/redo-coverage-common/coverage-ml/model/stagecoach-schema";
import {
  CoverageMlModelId,
  INTENT_V12_28_2025_COVERAGE_ML_MODEL_ID,
  STAGECOACH_COVERAGE_ML_MODEL_ID,
} from "@redotech/redo-coverage-common/coverage-ml/types";
import InfoIcon from "@redotech/redo-icons/info-circle_filled.svg";
import { PackageProtectionSettings } from "@redotech/redo-package-protection-settings/settings";
import { getMaxCoveragePriceByCartTotal } from "@redotech/redo-return-coverage-common/coverage-calculator/utils/max-cart-and-coverage";
import { PackageProtectionPricingStrategy } from "@redotech/redo-return-coverage-common/coverage-types";
import {
  ApprovalAvailabilityTime,
  AutomationStrategy,
  InsurerRejectionAction,
} from "@redotech/redo-shipping-claims-common/automation-strategy";
import { FraudZipCoverageExclusionStrategy } from "@redotech/redo-storefront-common/fraud-zip-coverage-exclusion-strategy";
import { groupInput, Input, input, InputSchema } from "@redotech/ui/form";
import { ValidationError } from "@redotech/ui/validator";
import { numberValidator } from "@redotech/ui/validators";
import {
  arrayEqual,
  dateEqual,
  nullableEqual,
  objectEqual,
  stringEqual,
} from "@redotech/util/equal";
import round from "lodash/round";
import { memo, ReactNode, useEffect, useMemo } from "react";
import { RedoAlert } from "../arbiter-components/alert/redo-alert";
import { RedoIncrementDecrement } from "../arbiter-components/increment-decrement/redo-increment-decrement";
import { Flex } from "../flex";
import { LabeledInput } from "../labeled-input";
import { SelectDropdown } from "../select-dropdown";
import { SettingSectionWrapper } from "../setting-wrapper/setting-section-wrapper";
import { SettingWrapper } from "../setting-wrapper/setting-wrapper";
import * as settingWrapperCss from "../setting-wrapper/setting-wrapper.module.css";
import { Text } from "../text";
import { FormTextInput } from "../text-input";
import {
  CoverageMlModelMode,
  minMaxValidator,
  PackageProtectionMlModelSettings,
  Pricing,
  pricingDefault,
  pricingFormValues,
  pricingRulesConvertTeamToValue,
  pricingRulesConvertValueToTeam,
} from "./dynamic-pricing";
import { variantMetafieldNamespaceValidator } from "./variant-metafield-exclusion-validator";

export const PP_PLUS_MIN_PRICE = 0.58;

export const DEFAULT_MAX_COVERAGE_ELIGIBLE_CART_TOTAL = 5000;

// The default 2% bracket applied to the $5000 default cart cap.
export const DEFAULT_MAX_COVERAGE_PRICE = "100";

export function getPpPlusMinPrice(redoKeepsDecimal: number): number {
  if (redoKeepsDecimal <= 0) {
    return Infinity;
  }
  return PP_PLUS_MIN_PRICE / redoKeepsDecimal;
}

// Managed claims (PP+) are like-for-like replacements where the customer never
// pays, so only these new-order-value strategies are offered. The other
// NonZeroValueExchangeStrategy values are built around upsell/charge/customer-
// payment concepts that degenerate to one of these on a claim. Shared between
// the admin dropdown and form hydration so a stale persisted value normalizes
// back into the allowlist instead of leaving the select with no matching option.
export const managedClaimNewOrderValueStrategies: readonly NonZeroValueExchangeStrategy[] =
  [
    NonZeroValueExchangeStrategy.NONE,
    NonZeroValueExchangeStrategy.APPLY_DISCOUNT,
    NonZeroValueExchangeStrategy.MARK_AS_PAID,
  ];

export const DEFAULT_MANAGED_CLAIM_NEW_ORDER_VALUE_STRATEGY =
  NonZeroValueExchangeStrategy.MARK_AS_PAID;

export function managedClaimNewOrderValueStrategyLabel(
  strategy: NonZeroValueExchangeStrategy,
): string {
  switch (strategy) {
    case NonZeroValueExchangeStrategy.NONE:
      return "Zero";
    case NonZeroValueExchangeStrategy.APPLY_DISCOUNT:
      return "Full value discounted to zero";
    case NonZeroValueExchangeStrategy.MARK_AS_PAID:
      return "Full value marked as paid";
    case NonZeroValueExchangeStrategy.DISCOUNTED_VALUE_MARKED_AS_PAID:
      return "Discounted value marked as paid";
    case NonZeroValueExchangeStrategy.UPSELL_VALUE_MARKED_AS_PAID:
      return "Upsell value marked as paid";
    case NonZeroValueExchangeStrategy.APPLY_EXCHANGE_DISCOUNT_WITH_TAX_MARK_AS_PAID:
      return "Full value with discount and tax applied, marked as paid";
    case NonZeroValueExchangeStrategy.CREATE_AND_COMPLETE_DRAFT_ORDER:
      return "Create and complete draft order";
    default:
      return strategy;
  }
}

// Collapse any persisted value outside the offered allowlist (legacy/unsupported
// strategies) to the default so the dropdown always has a matching option.
export function normalizeManagedClaimNewOrderValueStrategy(
  strategy: NonZeroValueExchangeStrategy | null | undefined,
): NonZeroValueExchangeStrategy {
  return strategy != null &&
    managedClaimNewOrderValueStrategies.includes(strategy)
    ? strategy
    : DEFAULT_MANAGED_CLAIM_NEW_ORDER_VALUE_STRATEGY;
}

export const MAX_PRICE_PER_ITEM_VARIANTS = 100;

// Cross-field checks for the price-per-item strategy. Errors are prefixed
// "Price per item:" so the pricing panel can surface them.
export const pricePerItemValidator = (value: any): ValidationError[] => {
  if (
    value.pricingStrategy !== PackageProtectionPricingStrategy.PRICE_PER_ITEM
  ) {
    return [];
  }
  const errors: ValidationError[] = [];
  const basePrice = Number(value.pricePerItemBasePrice);
  const additionalItemPrice = Number(value.pricePerItemAdditionalItemPrice);
  const perItemMaxPrice = Number(value.pricePerItemMaxPrice);
  if (value.pricePerItemBasePrice === "" || !(basePrice > 0)) {
    errors.push("Price per item: base price must be a positive number");
  }
  if (
    value.pricePerItemAdditionalItemPrice === "" ||
    isNaN(additionalItemPrice) ||
    additionalItemPrice < 0
  ) {
    errors.push(
      "Price per item: additional item price must be a non-negative number",
    );
  }
  if (value.pricePerItemMaxPrice === "" || !(perItemMaxPrice > 0)) {
    errors.push("Price per item: max price must be a positive number");
  }
  if (errors.length) {
    return errors;
  }
  if (perItemMaxPrice < basePrice) {
    errors.push("Price per item: max price must be at least the base price");
  }
  if (
    additionalItemPrice > 0 &&
    (perItemMaxPrice - basePrice) / additionalItemPrice + 1 >
      MAX_PRICE_PER_ITEM_VARIANTS
  ) {
    errors.push(
      `Price per item: pricing would need more than ${MAX_PRICE_PER_ITEM_VARIANTS} price variants; raise the additional item price or lower the max price`,
    );
  }
  return errors;
};

const packageProtectionValidator = (value: any) => {
  const errors = minMaxValidator(value);
  const merchantPaidThreshold =
    value.packageProtectionMerchantPaidCartThreshold;
  if (
    value.packageProtectionPlusEnabled &&
    !(Number(value.maxCoverageEligibleCartTotal) > 0)
  ) {
    errors.push(
      "Max cart total covered is required when Package Protection Plus is enabled",
    );
  }
  if (
    value.packageProtectionPlusMerchantPaidPerOrderEnabled &&
    value.coverage &&
    merchantPaidThreshold === ""
  ) {
    errors.push(
      "Merchant-Paid Minimum Cart Total is required when shopper coverage is enabled",
    );
  }
  if (merchantPaidThreshold !== "" && !value.coverage) {
    errors.push(
      "Shopper coverage must be enabled when a Merchant-Paid Minimum Cart Total is configured",
    );
  }
  if (merchantPaidThreshold !== "") {
    const threshold = Number(merchantPaidThreshold);
    if (
      value.minCoverageEligibleCartTotal != null &&
      threshold < value.minCoverageEligibleCartTotal
    ) {
      errors.push(
        "Merchant-Paid Minimum Cart Total must be at least the minimum eligible cart total",
      );
    }
    if (
      value.maxCoverageEligibleCartTotal != null &&
      threshold > value.maxCoverageEligibleCartTotal
    ) {
      errors.push(
        "Merchant-Paid Minimum Cart Total cannot exceed the maximum eligible cart total",
      );
    }
  }
  return errors;
};

function createPackageProtectionForm(validator: typeof minMaxValidator) {
  const inputs = {
    // General
    enabled: input<boolean>(),
    customerClaimsEnabled: input<boolean>(),
    automationStrategy: input<AutomationStrategy>(),
    approvalAvailabilityTime: input<ApprovalAvailabilityTime>(),
    // Holds hours in calendar mode, business days in business-days mode.
    timeTillApprovalAmount: input<string>({
      validator: numberValidator({ min: 1, max: 999 }),
    }),
    packageProtectionPlusPendingDocsCancel: input<boolean>(),
    packageProtectionPlusInsurerRejectionAction:
      input<InsurerRejectionAction>(),
    packageProtectionPlusEnabled: input<boolean>(),
    packageProtectionPlusPercentage: input<string>(),
    packageProtectionPlusAllOrdersCovered: input<boolean>(),
    packageProtectionPlusAllClaimsCovered: input<boolean>(),
    packageProtectionPlusShippingIncluded: input<boolean>(),
    packageProtectionPlusNewOrderValueStrategy:
      input<NonZeroValueExchangeStrategy>(),
    fraudZipCoverageExclusionStrategy:
      input<FraudZipCoverageExclusionStrategy>(),
    fraudReturnRateThresholdPercent: input<string>({
      validator: (value) => {
        const num = Number(value);
        if (value !== "" && (isNaN(num) || num <= 0 || num > 100)) {
          return ["Fraud return rate threshold must be between 0 and 100"];
        }
        return [];
      },
    }),
    fraudMinimumOrders: input<string>({
      validator: (value) => {
        const num = Number(value);
        if (value !== "" && (!Number.isInteger(num) || num <= 0)) {
          return ["Fraud minimum orders must be a positive whole number"];
        }
        return [];
      },
    }),
    newClaimsTables: input<boolean>(),
    packageProtectionPlusMerchantPaidPerOrderEnabled: input<boolean>(),
    packageProtectionMerchantPaidCartThreshold: input<string>({
      validator: (value) => {
        const num = Number(value);
        if (value !== "" && (isNaN(num) || num < 0)) {
          return ["Minimum cart total must be a non-negative number"];
        }
        return [];
      },
    }),
    packageProtectionPlusMerchantPaidPerOrderFee: input<string>({
      validator: (value) => {
        const num = Number(value);
        if (value !== "" && (isNaN(num) || num < 0)) {
          return ["Per-order fee must be a non-negative number"];
        }
        return [];
      },
    }),
    packageProtectionPlusMerchantPaidEnabledDates: input<
      { start: Date | null; end: Date | null }[]
    >({
      equal: arrayEqual(
        objectEqual({
          start: nullableEqual(dateEqual),
          end: nullableEqual(dateEqual),
        }),
      ),
      validator: (value) => {
        const errors: ValidationError[] = [];
        for (const range of value) {
          if (range.start == null) {
            errors.push("Required");
          } else if (range.start && range.end && range.start > range.end) {
            errors.push("Start must be before end");
          }
        }
        return errors;
      },
    }),
    // Coverage
    coverage: input<boolean>(),
    // Exclusions
    protectionExcludeTags: input<string[]>({ equal: arrayEqual(stringEqual) }),
    coverageExcludedCollections: input<string[]>({
      equal: arrayEqual(stringEqual),
    }),
    coverageExcludedVariantMetafields: input<string[]>({
      equal: arrayEqual(stringEqual),
      validator: variantMetafieldNamespaceValidator,
    }),
    minCoverageEligibleCartTotal: input<number | undefined>({
      validator: (value) => {
        if (value === undefined) {
          return [];
        }
        if (value < 0) {
          return ["Cart eligibility threshold must be a positive number"];
        }
        return [];
      },
    }),
    maxCoverageEligibleCartTotal: input<number | undefined>({
      validator: (value) => {
        if (value === undefined) {
          return [];
        }
        if (value <= 0) {
          return ["Cart eligibility threshold must be a positive number"];
        }
        return [];
      },
    }),
    fixedSubscriptionPrice: input<number | undefined>({
      validator: (value) => {
        if (value === undefined) {
          return [];
        }
        if (value < 0) {
          return ["Fixed subscription price must be a positive number"];
        }
        return [];
      },
    }),
    // Pricing
    ...pricingFormValues,
    pricingStrategy: input<PackageProtectionPricingStrategy | undefined>(),
    pricePerItemBasePrice: input<string>(),
    pricePerItemAdditionalItemPrice: input<string>(),
    pricePerItemMaxPrice: input<string>(),
    // ML Model
    coverageMlModelEnabled: input<boolean>(),
    coverageMlModelId: input<CoverageMlModelId>(),
    intentModelConfig: groupInput<{
      minPriceBiasPercentOfConstraintRange: Input<string>;
      maxPriceBiasPercentOfConstraintRange: Input<string>;
    }>({
      minPriceBiasPercentOfConstraintRange: input<string>({
        validator: numberValidator({ min: -1, max: 1 }),
      }),
      maxPriceBiasPercentOfConstraintRange: input<string>({
        validator: numberValidator({ min: -1, max: 1 }),
      }),
    }),
    stagecoachPriceVsConversionWeight: input<string>({
      validator: numberValidator({ min: 0 }),
    }),
    stagecoachThompsonSamplingEnabled: input<boolean>(),
    stagecoachInferenceMode: input<StagecoachInferenceMode | undefined>(),
  };
  return groupInput(inputs, {
    validator: (value: any) => [
      ...validator(value),
      ...pricePerItemValidator(value),
    ],
  });
}

export const packageProtectionForm = createPackageProtectionForm(
  packageProtectionValidator,
);

export const packageProtectionFormWithoutPpPlusPriceFloor =
  createPackageProtectionForm(minMaxValidator);

export type PackageProtectionForm = InputSchema.Input<
  typeof packageProtectionForm
>;

export type PackageProtectionValue = InputSchema.Value<
  typeof packageProtectionForm
>;

export const packageProtectionDefault: PackageProtectionValue = {
  // General
  enabled: false,
  customerClaimsEnabled: false,
  automationStrategy: AutomationStrategy.AUTO_REJECT_APPROVE,
  approvalAvailabilityTime: ApprovalAvailabilityTime.ONLY_BUSINESS_DAYS,
  timeTillApprovalAmount: "3",
  packageProtectionPlusPendingDocsCancel: true,
  packageProtectionPlusInsurerRejectionAction: InsurerRejectionAction.REJECT,
  packageProtectionPlusEnabled: false,
  packageProtectionPlusPercentage: "100",
  packageProtectionPlusAllOrdersCovered: false,
  packageProtectionPlusAllClaimsCovered: false,
  packageProtectionPlusShippingIncluded: true,
  packageProtectionPlusNewOrderValueStrategy:
    DEFAULT_MANAGED_CLAIM_NEW_ORDER_VALUE_STRATEGY,
  fraudZipCoverageExclusionStrategy: FraudZipCoverageExclusionStrategy.NO_OP,
  fraudReturnRateThresholdPercent: "",
  fraudMinimumOrders: "",
  newClaimsTables: true,
  packageProtectionPlusMerchantPaidPerOrderEnabled: false,
  packageProtectionPlusMerchantPaidPerOrderFee: "",
  packageProtectionMerchantPaidCartThreshold: "",
  packageProtectionPlusMerchantPaidEnabledDates: [],
  // Coverage
  coverage: false,
  // Exclusions
  protectionExcludeTags: [],
  coverageExcludedCollections: [],
  coverageExcludedVariantMetafields: [],
  minCoverageEligibleCartTotal: undefined,
  maxCoverageEligibleCartTotal: DEFAULT_MAX_COVERAGE_ELIGIBLE_CART_TOTAL,
  fixedSubscriptionPrice: undefined,
  // Pricing
  ...pricingDefault,
  maxPrice: DEFAULT_MAX_COVERAGE_PRICE,
  pricingStrategy: undefined,
  pricePerItemBasePrice: "",
  pricePerItemAdditionalItemPrice: "",
  pricePerItemMaxPrice: "",
  // ML Model
  coverageMlModelEnabled: false,
  coverageMlModelId: INTENT_V12_28_2025_COVERAGE_ML_MODEL_ID,
  intentModelConfig: {
    minPriceBiasPercentOfConstraintRange: "",
    maxPriceBiasPercentOfConstraintRange: "",
  },
  stagecoachPriceVsConversionWeight: "1.0",
  stagecoachThompsonSamplingEnabled: false,
  stagecoachInferenceMode: undefined,
};

type PricingRuleSetValue = PackageProtectionValue["pricingRuleSet"];

function computeImpliedMaxPrice(
  cartTotal: number,
  pricingRuleSetValue: PricingRuleSetValue,
  backupPercentageNum: number,
): number | null {
  if (!Number.isFinite(cartTotal) || cartTotal <= 0) {
    return null;
  }
  const implied = getMaxCoveragePriceByCartTotal({
    cartTotal,
    backupPercentage: backupPercentageNum,
    pricingRuleSet: pricingRulesConvertValueToTeam(pricingRuleSetValue),
  });
  return typeof implied === "number" && implied > 0 ? round(implied, 2) : null;
}

/**
 * Whether a save lowers the PP+ max cart total covered — and so the derived
 * max coverage price. Such saves need confirmation (finance approval).
 */
export function isLoweringPpPlusMaxCartTotal({
  initial,
  current,
}: {
  initial:
    | Pick<PackageProtectionValue, "maxCoverageEligibleCartTotal">
    | undefined;
  current: Pick<
    PackageProtectionValue,
    "packageProtectionPlusEnabled" | "maxCoverageEligibleCartTotal"
  >;
}): boolean {
  if (!current.packageProtectionPlusEnabled) {
    return false;
  }
  const from = initial?.maxCoverageEligibleCartTotal;
  const to = current.maxCoverageEligibleCartTotal;
  return typeof from === "number" && typeof to === "number" && to < from;
}

/**
 * PP+ pricing anchor: merchants edit the max cart total covered and the
 * pricing brackets; the max coverage price is always derived from those two.
 */
export function useDerivedMaxPrice({
  ppPlusEnabled,
  maxPriceInput,
  maxCartInput,
  pricingRuleSetValue,
  backupPercentageNum,
}: {
  ppPlusEnabled: boolean;
  maxPriceInput: Input<string>;
  maxCartInput: Input<number | undefined>;
  pricingRuleSetValue: PricingRuleSetValue;
  backupPercentageNum: number;
}): {
  setMaxCartLinked: (next: number | undefined) => void;
  impliedMaxPrice: number | null;
} {
  const impliedMaxPrice = computeImpliedMaxPrice(
    maxCartInput.value ?? NaN,
    pricingRuleSetValue,
    backupPercentageNum,
  );

  const pricingRuleSetKey = useMemo(
    () => JSON.stringify(pricingRuleSetValue),
    [pricingRuleSetValue],
  );

  const setMaxCartLinked = (next: number | undefined) => {
    maxCartInput.setValue(next);
    if (!ppPlusEnabled || typeof next !== "number" || next <= 0) {
      return;
    }
    const implied = computeImpliedMaxPrice(
      next,
      pricingRuleSetValue,
      backupPercentageNum,
    );
    if (implied !== null) {
      maxPriceInput.setValue(String(implied));
    }
  };

  // Brackets/backup % → maxPrice: pricing edits rewrite the derived price
  // while the cart cap stays fixed (setMaxCartLinked covers cart edits).
  useEffect(() => {
    if (!ppPlusEnabled) {
      return;
    }
    const next = computeImpliedMaxPrice(
      maxCartInput.value ?? NaN,
      pricingRuleSetValue,
      backupPercentageNum,
    );
    if (next !== null && next !== Number(maxPriceInput.value)) {
      maxPriceInput.setValue(String(next));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ppPlusEnabled, pricingRuleSetKey, backupPercentageNum]);

  return { setMaxCartLinked, impliedMaxPrice };
}

// True when any price bracket has a current validation error — bracket value
// outside min/max (emitted as `${rulesetIndex},${bracketIndex}` on the form's
// top-level errors) or a percentage bracket exceeding 100 (emitted as
// `${bracketIndex}` on the per-ruleset errors). Until the merchant fixes those,
// the derived cart cap would be misleading, so we hide the hint.
function pricingBracketsHaveErrors(input: PackageProtectionForm): boolean {
  const minMaxError = input.errors.some(
    (e) => typeof e === "string" && /^\d+,\d+$/.test(e),
  );
  if (minMaxError) {
    return true;
  }
  return input.inputs.pricingRuleSet.inputs.some((ruleSet) =>
    ruleSet.errors.some((e) => typeof e === "string" && /^\d+$/.test(e)),
  );
}

function formatDollars(value: number, fractionDigits = 0) {
  return `$${value.toLocaleString(undefined, {
    maximumFractionDigits: fractionDigits,
    minimumFractionDigits: 0,
  })}`;
}

const LinkedFieldHint = memo(function LinkedFieldHint({
  title,
  body,
}: {
  title: string;
  body: string;
}) {
  return (
    <Flex mt="sm">
      <RedoAlert
        color="gray"
        description={body}
        Icon={InfoIcon}
        size="sm"
        title={title}
      />
    </Flex>
  );
});

export type PackageProtectionMinPriceWarning =
  | { kind: "ppPlusFloor" }
  | { kind: "revShare"; redoKeeps: number; minPrice: number };

export interface PackageProtectionPricingState {
  ppPlusEnabled: boolean;
  strategyLocked: boolean;
  setMaxCartLinked: (next: number | undefined) => void;
  impliedMaxPrice: number | null;
  showDerivedPriceHint: boolean;
  minPriceWarning: PackageProtectionMinPriceWarning | null;
  maxCartError: string | undefined;
  perItemSelected: boolean;
  pricePerItemErrors: string[];
  /** Off when an ML model other than Intent sets the price. */
  showPriceTiers: boolean;
}

/** PP+ rules the pricing editors share: the strategy lock, the derived max price and the min price floors. */
export function usePackageProtectionPricingState(
  input: PackageProtectionForm,
  adminApp: boolean,
): PackageProtectionPricingState {
  const ppPlusEnabled = input.value.packageProtectionPlusEnabled;
  const { setMaxCartLinked, impliedMaxPrice } = useDerivedMaxPrice({
    ppPlusEnabled,
    maxPriceInput: input.inputs.maxPrice,
    maxCartInput: input.inputs.maxCoverageEligibleCartTotal,
    pricingRuleSetValue: input.value.pricingRuleSet,
    backupPercentageNum: Number(input.value.percentage) / 100,
  });
  const minPrice = Number(input.value.minPrice);
  const redoKeeps = Number(input.value.packageProtectionPlusPercentage) / 100;
  const revShareMinPrice =
    redoKeeps > 0 ? getPpPlusMinPrice(redoKeeps) : undefined;
  let minPriceWarning: PackageProtectionMinPriceWarning | null = null;
  if (ppPlusEnabled && minPrice < PP_PLUS_MIN_PRICE) {
    minPriceWarning = { kind: "ppPlusFloor" };
  } else if (
    ppPlusEnabled &&
    revShareMinPrice !== undefined &&
    revShareMinPrice > PP_PLUS_MIN_PRICE &&
    minPrice < revShareMinPrice
  ) {
    minPriceWarning = {
      kind: "revShare",
      redoKeeps,
      minPrice: revShareMinPrice,
    };
  }
  return {
    ppPlusEnabled,
    // PP+ pricing is admin-only server-side, so a merchant edit would 400.
    strategyLocked: ppPlusEnabled && !adminApp,
    setMaxCartLinked,
    impliedMaxPrice,
    showDerivedPriceHint:
      !pricingBracketsHaveErrors(input) && impliedMaxPrice !== null,
    minPriceWarning,
    maxCartError: input.errors.find(
      (e) => typeof e === "string" && e.includes("Max cart total"),
    ),
    perItemSelected:
      input.value.pricingStrategy ===
      PackageProtectionPricingStrategy.PRICE_PER_ITEM,
    pricePerItemErrors: input.errors.filter(
      (e): e is string =>
        typeof e === "string" && e.startsWith("Price per item:"),
    ),
    showPriceTiers:
      !input.value.coverageMlModelEnabled ||
      input.value.coverageMlModelId === INTENT_V12_28_2025_COVERAGE_ML_MODEL_ID,
  };
}

export const PackageProtectionPricing = memo(function PackageProtectionPricing({
  input,
  canEditSettings,
  coverageMlModelMode,
  adminApp = false,
  currencyDisplay,
  email,
}: {
  input: PackageProtectionForm;
  canEditSettings: boolean;
  coverageMlModelMode: CoverageMlModelMode;
  adminApp?: boolean;
  currencyDisplay?: string;
  email?: string;
}) {
  const {
    ppPlusEnabled,
    strategyLocked: pricingStrategyLocked,
    setMaxCartLinked,
    impliedMaxPrice,
    showDerivedPriceHint,
    minPriceWarning,
    maxCartError: ppPlusMaxCartError,
    perItemSelected,
    pricePerItemErrors,
    showPriceTiers,
  } = usePackageProtectionPricingState(input, adminApp);

  const minPriceFooter: ReactNode =
    minPriceWarning?.kind === "ppPlusFloor" ? (
      <Flex mt="sm">
        <RedoAlert
          color="warning"
          description={`A minimum price of at least $${PP_PLUS_MIN_PRICE.toFixed(2)} is recommended when Package Protection Plus is enabled. You can still save these settings.`}
          size="sm"
          title="Min price below recommended PP+ minimum"
        />
      </Flex>
    ) : minPriceWarning?.kind === "revShare" ? (
      <Flex mt="sm">
        <RedoAlert
          color="warning"
          description={`Redo only keeps ${(minPriceWarning.redoKeeps * 100).toFixed(0)}% of PP+ revenue. To net $${PP_PLUS_MIN_PRICE.toFixed(2)} per order, min price should be at least $${minPriceWarning.minPrice.toFixed(2)}.`}
          size="sm"
          title="Min price below revenue-share floor"
        />
      </Flex>
    ) : null;

  const maxPriceFooter: ReactNode = ppPlusEnabled ? (
    <LinkedFieldHint
      body={
        showDerivedPriceHint
          ? "Auto-calculated from the max cart total covered and the pricing brackets. Edit those to change it."
          : "Auto-calculated from the max cart total covered and the pricing brackets. Set a max cart total to update this price."
      }
      title="Derived from max cart total (PP+)"
    />
  ) : null;

  const maxCartHint: ReactNode = ppPlusMaxCartError ? (
    <Flex mt="sm">
      <RedoAlert color="error" size="sm" title={String(ppPlusMaxCartError)} />
    </Flex>
  ) : ppPlusEnabled ? (
    <LinkedFieldHint
      body={
        showDerivedPriceHint && impliedMaxPrice !== null
          ? `Max coverage price auto-syncs to ${formatDollars(impliedMaxPrice, 2)} based on this cart total and the pricing brackets.`
          : "PP+ derives the max coverage price from this cart total and the pricing brackets."
      }
      title="Controls max coverage price (PP+)"
    />
  ) : null;

  return (
    <>
      <SettingSectionWrapper id="pricing-strategy">
        <Flex dir="column" gap="xs" pb="lg" w="full">
          <SettingWrapper compact id="pricing-strategy-select">
            <LabeledInput label="Pricing strategy">
              <SelectDropdown
                disabled={!canEditSettings || pricingStrategyLocked}
                options={Object.values(PackageProtectionPricingStrategy)}
                value={
                  input.value.pricingStrategy ??
                  PackageProtectionPricingStrategy.CART_VALUE
                }
                valueChange={(value) =>
                  input.inputs.pricingStrategy.setValue(value)
                }
              >
                {(option) => {
                  switch (option) {
                    case PackageProtectionPricingStrategy.CART_VALUE:
                      return "Cart value";
                    case PackageProtectionPricingStrategy.PRICE_PER_ITEM:
                      return "Price per item";
                  }
                }}
              </SelectDropdown>
            </LabeledInput>
          </SettingWrapper>
          {pricingStrategyLocked ? (
            <LinkedFieldHint
              body="Contact a Redo admin to change the pricing strategy for your account."
              title="Set by Redo (PP+)"
            />
          ) : null}
        </Flex>
      </SettingSectionWrapper>
      {perItemSelected ? (
        <SettingSectionWrapper id="price-per-item">
          <Flex dir="column" gap="md" pb="lg" w="full">
            <Text fontSize="md">Price per item</Text>
            <Flex align="flex-start" dir="row" flex="1" gap="md">
              <Flex flex={1}>
                <SettingWrapper
                  className={settingWrapperCss.fill}
                  compact
                  id="price-per-item-base-price"
                >
                  <FormTextInput
                    allowInvalidNumbers
                    description="Price with one item in the cart"
                    disabled={!canEditSettings}
                    input={input.inputs.pricePerItemBasePrice}
                    label="Base price"
                    prefix={currencyDisplay}
                    type="number"
                  />
                </SettingWrapper>
              </Flex>
              <Flex flex={1}>
                <SettingWrapper
                  className={settingWrapperCss.fill}
                  compact
                  id="price-per-item-additional-item-price"
                >
                  <FormTextInput
                    allowInvalidNumbers
                    description="Added for each item after the first"
                    disabled={!canEditSettings}
                    input={input.inputs.pricePerItemAdditionalItemPrice}
                    label="Additional item price"
                    prefix={currencyDisplay}
                    type="number"
                  />
                </SettingWrapper>
              </Flex>
              <Flex flex={1}>
                <SettingWrapper
                  className={settingWrapperCss.fill}
                  compact
                  id="price-per-item-max-price"
                >
                  <FormTextInput
                    allowInvalidNumbers
                    description="Cap on the total price"
                    disabled={!canEditSettings}
                    input={input.inputs.pricePerItemMaxPrice}
                    label="Max price"
                    prefix={currencyDisplay}
                    type="number"
                  />
                </SettingWrapper>
              </Flex>
            </Flex>
            {pricePerItemErrors.map((error) => (
              <Flex key={error} mt="sm">
                <RedoAlert color="error" size="sm" title={error} />
              </Flex>
            ))}
          </Flex>
        </SettingSectionWrapper>
      ) : (
        <>
          <PackageProtectionMlModelSettings
            canEditSettings={canEditSettings}
            coverageMlModelEnabled={input.inputs.coverageMlModelEnabled}
            coverageMlModelId={input.inputs.coverageMlModelId}
            coverageMlModelMode={coverageMlModelMode}
            currencyDisplay={currencyDisplay}
            email={email}
            intentModelConfig={input.inputs.intentModelConfig}
            maxPriceDisabled={ppPlusEnabled}
            maxPriceFooter={maxPriceFooter}
            maxPriceInput={input.inputs.maxPrice}
            minPriceFooter={minPriceFooter}
            minPriceInput={input.inputs.minPrice}
            stagecoachPriceVsConversionWeight={
              input.inputs.stagecoachPriceVsConversionWeight
            }
            stagecoachThompsonSamplingEnabled={
              input.inputs.stagecoachThompsonSamplingEnabled
            }
          />
          {showPriceTiers && (
            <SettingSectionWrapper id="pricing">
              <Pricing
                adminApp={adminApp}
                canEditSettings={canEditSettings}
                currencyDisplay={currencyDisplay}
                input={input}
                maxPriceDisabled={ppPlusEnabled}
                maxPriceFooter={maxPriceFooter}
                maxPriceInput={input.inputs.maxPrice}
                minPriceFooter={minPriceFooter}
              />
            </SettingSectionWrapper>
          )}
        </>
      )}
      <SettingSectionWrapper id="cart-boundaries">
        <Flex dir="column" gap="xs" w="full">
          <Text fontSize="md">Cart total boundaries</Text>
          <Flex align="flex-start" dir="row" flex="1" gap="md">
            <Flex flex={1}>
              <SettingWrapper
                className={settingWrapperCss.fill}
                compact
                id="min-cart-total"
              >
                <RedoIncrementDecrement
                  disabled={!canEditSettings}
                  error={
                    input.inputs.minCoverageEligibleCartTotal.errors.length > 0
                  }
                  fullWidth
                  min={0}
                  placeholder="No minimum"
                  prefix={
                    typeof input.value.minCoverageEligibleCartTotal === "number"
                      ? "$"
                      : ""
                  }
                  selectOnFocus
                  setValue={input.inputs.minCoverageEligibleCartTotal.setValue}
                  size="regular"
                  step="any"
                  value={input.value.minCoverageEligibleCartTotal}
                />
              </SettingWrapper>
            </Flex>
            <Flex dir="column" flex={1} gap="xs">
              <SettingWrapper compact id="max-cart-total">
                <RedoIncrementDecrement
                  disabled={!canEditSettings}
                  error={
                    input.inputs.maxCoverageEligibleCartTotal.errors.length >
                      0 || ppPlusMaxCartError !== undefined
                  }
                  fullWidth
                  min={0}
                  placeholder="No maximum"
                  prefix={
                    typeof input.value.maxCoverageEligibleCartTotal === "number"
                      ? "$"
                      : ""
                  }
                  selectOnFocus
                  setValue={setMaxCartLinked}
                  size="regular"
                  step="any"
                  value={input.value.maxCoverageEligibleCartTotal}
                />
              </SettingWrapper>
              {maxCartHint}
            </Flex>
          </Flex>
          <Text fontSize="sm">
            Package protection will not be offered if the cart total is not
            within these boundaries
          </Text>
        </Flex>
      </SettingSectionWrapper>
      <SettingSectionWrapper id="subscription">
        <Flex dir="column" gap="xs" pt="lg" w="full">
          <Text fontSize="md">Fixed subscription price</Text>
          <SettingWrapper compact id="fixed-subscription-price">
            <RedoIncrementDecrement
              disabled={!canEditSettings}
              error={input.inputs.fixedSubscriptionPrice.errors.length > 0}
              fullWidth
              min={0}
              placeholder="No fixed price"
              prefix={
                typeof input.value.fixedSubscriptionPrice === "number"
                  ? "$"
                  : ""
              }
              selectOnFocus
              setValue={input.inputs.fixedSubscriptionPrice.setValue}
              size="regular"
              step="any"
              value={input.value.fixedSubscriptionPrice}
            />
          </SettingWrapper>
          <Text fontSize="sm">
            Set a fixed price for package protection when there are subscription
            items in the cart
          </Text>
        </Flex>
      </SettingSectionWrapper>
    </>
  );
});

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;

function resolveApprovalAvailabilityTime(
  team: Pick<
    TeamWith<{ packageProtection?: PackageProtectionSettings }>,
    "settings"
  >,
): ApprovalAvailabilityTime {
  const options = team.settings.packageProtection?.automationStrategy?.options;
  if (options?.approvalAvailabilityTime) {
    return options.approvalAvailabilityTime;
  }
  // Legacy configs that already had an hours-based timer keep counting hours;
  // brand-new configs default to business days.
  return options?.timeTillApprovalMs != null
    ? ApprovalAvailabilityTime.INCLUDE_HOLIDAYS_WEEKENDS
    : ApprovalAvailabilityTime.ONLY_BUSINESS_DAYS;
}

function timeTillApprovalAmountFromTeam(
  team: Pick<
    TeamWith<{ packageProtection?: PackageProtectionSettings }>,
    "settings"
  >,
  availability: ApprovalAvailabilityTime,
): string {
  const isBusinessDays =
    availability === ApprovalAvailabilityTime.ONLY_BUSINESS_DAYS;
  const ms =
    team.settings.packageProtection?.automationStrategy?.options
      ?.timeTillApprovalMs;
  if (!ms) {
    return isBusinessDays ? "3" : "72";
  }
  return String(ms / (isBusinessDays ? DAY_MS : HOUR_MS));
}

function optionalNumberSetting(value: string): number | null {
  return value.trim() === "" ? null : Number(value);
}

/** Blank inputs clear the override so the automated analysis defaults apply. */
export function fraudDetectionThresholdsFormValueToTeam(
  packageProtection: Pick<
    PackageProtectionValue,
    "fraudReturnRateThresholdPercent" | "fraudMinimumOrders"
  >,
): { returnRateThresholdPercent: number | null; minimumOrders: number | null } {
  return {
    returnRateThresholdPercent: optionalNumberSetting(
      packageProtection.fraudReturnRateThresholdPercent,
    ),
    minimumOrders: optionalNumberSetting(packageProtection.fraudMinimumOrders),
  };
}

export function packageProtectionTeamToFormValue(
  team: Pick<
    TeamWith<{
      packageProtection?: PackageProtectionSettings;
      countriesPackageProtection: string[];
      protectionExcludeTags?: string[];
      shopifyExtension?: {
        fraudZip?: {
          fraudZipCoverageExclusionStrategy?: FraudZipCoverageExclusionStrategy;
          returnRateThresholdPercent?: number | null;
          minimumOrders?: number | null;
        };
      };
    }>,
    "settings"
  >,
): PackageProtectionValue {
  const approvalAvailabilityTime = resolveApprovalAvailabilityTime(team);
  return {
    // General
    enabled: team.settings.packageProtection?.enabled || false,
    customerClaimsEnabled:
      team.settings.packageProtection?.customerClaimsEnabled || false,
    automationStrategy:
      team.settings.packageProtection?.automationStrategy?.strategy ||
      AutomationStrategy.TIME_BASED_APPROVAL,
    approvalAvailabilityTime,
    timeTillApprovalAmount: timeTillApprovalAmountFromTeam(
      team,
      approvalAvailabilityTime,
    ),
    // On for teams configuring package protection for the first time; teams
    // with existing settings keep their saved value (off pre-dates the field).
    packageProtectionPlusPendingDocsCancel: team.settings.packageProtection
      ? (team.settings.packageProtection
          .packageProtectionPlusPendingDocsCancel ?? false)
      : true,
    packageProtectionPlusInsurerRejectionAction:
      team.settings.packageProtection
        ?.packageProtectionPlusInsurerRejectionAction ??
      InsurerRejectionAction.REJECT,
    packageProtectionPlusEnabled:
      team.settings.packageProtection?.packageProtectionPlusEnabled || false,
    packageProtectionPlusPercentage:
      team.settings.packageProtection?.packageProtectionPlusPercentage != null
        ? String(
            round(
              team.settings.packageProtection.packageProtectionPlusPercentage *
                100.0,
              3,
            ),
          )
        : "100",
    packageProtectionPlusAllOrdersCovered:
      team.settings.packageProtection?.packageProtectionPlusAllOrdersCovered ||
      false,
    packageProtectionPlusAllClaimsCovered:
      team.settings.packageProtection?.packageProtectionPlusAllClaimsCovered ||
      false,
    packageProtectionPlusShippingIncluded:
      team.settings.packageProtection?.packageProtectionPlusShippingIncluded ??
      true,
    packageProtectionPlusNewOrderValueStrategy:
      normalizeManagedClaimNewOrderValueStrategy(
        team.settings.packageProtection
          ?.packageProtectionPlusNewOrderValueStrategy,
      ),
    fraudZipCoverageExclusionStrategy:
      team.settings.shopifyExtension?.fraudZip
        ?.fraudZipCoverageExclusionStrategy ??
      FraudZipCoverageExclusionStrategy.NO_OP,
    fraudReturnRateThresholdPercent:
      team.settings.shopifyExtension?.fraudZip?.returnRateThresholdPercent?.toString() ??
      "",
    fraudMinimumOrders:
      team.settings.shopifyExtension?.fraudZip?.minimumOrders?.toString() ?? "",
    newClaimsTables: team.settings.packageProtection?.newClaimsTables ?? true,
    packageProtectionPlusMerchantPaidPerOrderEnabled:
      team.settings.packageProtection
        ?.packageProtectionPlusMerchantPaidPerOrderEnabled ?? false,
    packageProtectionPlusMerchantPaidPerOrderFee:
      team.settings.packageProtection?.packageProtectionPlusMerchantPaidPerOrderFee?.toString() ??
      "",
    packageProtectionMerchantPaidCartThreshold:
      team.settings.packageProtection?.packageProtectionMerchantPaidCartThreshold?.toString() ??
      "",
    packageProtectionPlusMerchantPaidEnabledDates:
      team.settings.packageProtection?.packageProtectionPlusMerchantPaidEnabledDates?.map(
        (range) => ({
          start: range.start ? new Date(Date.parse(range.start)) : null,
          end: range.end ? new Date(Date.parse(range.end)) : null,
        }),
      ) ?? [],
    // Coverage
    coverage: team.settings.packageProtection?.coverage || false,
    // Exclusions
    protectionExcludeTags: team.settings.protectionExcludeTags || [],
    coverageExcludedCollections:
      team.settings.packageProtection?.coverageExcludedCollections || [],
    coverageExcludedVariantMetafields:
      team.settings.packageProtection?.coverageExcludedVariantMetafields || [],
    minPrice: team.settings.packageProtection?.minPrice?.toString() || "1",
    maxPrice:
      team.settings.packageProtection?.maxPrice?.toString() ||
      DEFAULT_MAX_COVERAGE_PRICE,
    percentage: team.settings.packageProtection?.percentage
      ? String(round(team.settings.packageProtection.percentage * 100.0, 3))
      : "2",
    pricingRuleSet: pricingRulesConvertTeamToValue(
      team.settings.packageProtection?.pricingRuleSet,
      team.settings.countriesPackageProtection,
      team.settings.packageProtection?.percentage,
    ),
    // Left unset when the team has no stored strategy. PP+ locks this field, so
    // materializing the implied cart value here would block every save.
    pricingStrategy: team.settings.packageProtection?.pricingStrategy,
    pricePerItemBasePrice:
      team.settings.packageProtection?.pricePerItem?.basePrice?.toString() ??
      "",
    pricePerItemAdditionalItemPrice:
      team.settings.packageProtection?.pricePerItem?.pricePerAdditionalItem?.toString() ??
      "",
    pricePerItemMaxPrice:
      team.settings.packageProtection?.pricePerItem?.maxPrice?.toString() ?? "",
    internationalPricingEnabled: team.settings.packageProtection?.pricingRuleSet
      ?.length
      ? team.settings.packageProtection?.pricingRuleSet?.length > 1
      : false,
    minCoverageEligibleCartTotal:
      team.settings.packageProtection?.minCoverageEligibleCartTotal,
    // Default the cart cap only for teams configuring package protection for
    // the first time; existing configs keep their stored (possibly unset) cap.
    maxCoverageEligibleCartTotal:
      team.settings.packageProtection?.maxCoverageEligibleCartTotal ??
      (team.settings.packageProtection
        ? undefined
        : DEFAULT_MAX_COVERAGE_ELIGIBLE_CART_TOTAL),
    fixedSubscriptionPrice:
      team.settings.packageProtection?.fixedSubscriptionPrice,
    coverageMlModelEnabled: !!team.settings.packageProtection?.coverageMlModel,
    coverageMlModelId:
      team.settings.packageProtection?.coverageMlModel?.id ||
      INTENT_V12_28_2025_COVERAGE_ML_MODEL_ID,
    intentModelConfig: {
      minPriceBiasPercentOfConstraintRange:
        team.settings.packageProtection?.coverageMlModel?.id ===
        INTENT_V12_28_2025_COVERAGE_ML_MODEL_ID
          ? team.settings.packageProtection?.coverageMlModel?.config?.minPriceBiasPercentOfConstraintRange?.toString() ||
            ""
          : "",
      maxPriceBiasPercentOfConstraintRange:
        team.settings.packageProtection?.coverageMlModel?.id ===
        INTENT_V12_28_2025_COVERAGE_ML_MODEL_ID
          ? team.settings.packageProtection?.coverageMlModel?.config?.maxPriceBiasPercentOfConstraintRange?.toString() ||
            ""
          : "",
    },
    stagecoachPriceVsConversionWeight:
      team.settings.packageProtection?.coverageMlModel?.id ===
      STAGECOACH_COVERAGE_ML_MODEL_ID
        ? team.settings.packageProtection?.coverageMlModel?.config?.priceVsConversionWeight?.toString() ||
          "1.0"
        : "1.0",
    stagecoachThompsonSamplingEnabled:
      team.settings.packageProtection?.coverageMlModel?.id ===
      STAGECOACH_COVERAGE_ML_MODEL_ID
        ? (team.settings.packageProtection?.coverageMlModel?.config
            ?.thompsonSamplingEnabled ?? false)
        : false,
    stagecoachInferenceMode:
      team.settings.packageProtection?.coverageMlModel?.id ===
      STAGECOACH_COVERAGE_ML_MODEL_ID
        ? (team.settings.packageProtection?.coverageMlModel?.config
            ?.inferenceMode ?? undefined)
        : undefined,
  };
}

export function packageProtectionFormValueToTeam(
  packageProtection: PackageProtectionValue,
  splitProducts: boolean,
): PackageProtectionSettings {
  return {
    enabled: packageProtection.enabled,
    coverage: packageProtection.coverage,
    customerClaimsEnabled: packageProtection.customerClaimsEnabled,
    automationStrategy: {
      strategy: packageProtection.automationStrategy,
      options: {
        timeTillApprovalMs: Temporal.Duration.from(
          packageProtection.approvalAvailabilityTime ===
            ApprovalAvailabilityTime.ONLY_BUSINESS_DAYS
            ? { days: Number(packageProtection.timeTillApprovalAmount) }
            : { hours: Number(packageProtection.timeTillApprovalAmount) },
        ).total({ unit: "milliseconds" }),
        approvalAvailabilityTime: packageProtection.approvalAvailabilityTime,
      },
    },
    packageProtectionPlusEnabled:
      packageProtection.packageProtectionPlusEnabled,
    packageProtectionPlusAllOrdersCovered:
      packageProtection.packageProtectionPlusAllOrdersCovered,
    packageProtectionPlusAllClaimsCovered:
      packageProtection.packageProtectionPlusAllClaimsCovered,
    packageProtectionPlusPercentage:
      round(+packageProtection.packageProtectionPlusPercentage, 3) / 100.0,
    packageProtectionPlusShippingIncluded:
      packageProtection.packageProtectionPlusShippingIncluded,
    packageProtectionPlusPendingDocsCancel:
      packageProtection.packageProtectionPlusPendingDocsCancel,
    packageProtectionPlusInsurerRejectionAction:
      packageProtection.packageProtectionPlusInsurerRejectionAction,
    packageProtectionPlusNewOrderValueStrategy:
      packageProtection.packageProtectionPlusNewOrderValueStrategy,
    newClaimsTables: packageProtection.newClaimsTables,
    packageProtectionPlusMerchantPaidPerOrderEnabled:
      packageProtection.packageProtectionPlusMerchantPaidPerOrderEnabled,
    packageProtectionPlusMerchantPaidPerOrderFee:
      packageProtection.packageProtectionPlusMerchantPaidPerOrderFee !== ""
        ? Number(packageProtection.packageProtectionPlusMerchantPaidPerOrderFee)
        : undefined,
    packageProtectionMerchantPaidCartThreshold:
      packageProtection.packageProtectionMerchantPaidCartThreshold === ""
        ? undefined
        : Number(packageProtection.packageProtectionMerchantPaidCartThreshold),
    packageProtectionPlusMerchantPaidEnabledDates:
      packageProtection.packageProtectionPlusMerchantPaidEnabledDates.map(
        (range) => ({
          start: range.start?.toISOString() ?? null,
          end: range.end?.toISOString() ?? null,
        }),
      ),
    minPrice: Number(packageProtection.minPrice),
    maxPrice: Number(packageProtection.maxPrice),
    percentage: round(+packageProtection.percentage, 3) / 100.0,
    splitProducts,
    coverageExcludedCollections: packageProtection.coverageExcludedCollections,
    coverageExcludedVariantMetafields:
      packageProtection.coverageExcludedVariantMetafields,
    pricingRuleSet: pricingRulesConvertValueToTeam(
      packageProtection.pricingRuleSet,
    ),
    pricingStrategy: packageProtection.pricingStrategy,
    // Persist populated per-item values regardless of the selected strategy so
    // toggling strategies doesn't destroy them.
    pricePerItem:
      packageProtection.pricePerItemBasePrice !== "" &&
      packageProtection.pricePerItemAdditionalItemPrice !== "" &&
      packageProtection.pricePerItemMaxPrice !== ""
        ? {
            basePrice: Number(packageProtection.pricePerItemBasePrice),
            pricePerAdditionalItem: Number(
              packageProtection.pricePerItemAdditionalItemPrice,
            ),
            maxPrice: Number(packageProtection.pricePerItemMaxPrice),
          }
        : undefined,
    minCoverageEligibleCartTotal: packageProtection.minCoverageEligibleCartTotal
      ? packageProtection.minCoverageEligibleCartTotal
      : undefined,
    maxCoverageEligibleCartTotal: packageProtection.maxCoverageEligibleCartTotal
      ? packageProtection.maxCoverageEligibleCartTotal
      : undefined,
    fixedSubscriptionPrice: packageProtection.fixedSubscriptionPrice
      ? packageProtection.fixedSubscriptionPrice
      : undefined,
    coverageMlModel:
      packageProtection.coverageMlModelEnabled &&
      packageProtection.coverageMlModelId
        ? packageProtection.coverageMlModelId ===
          INTENT_V12_28_2025_COVERAGE_ML_MODEL_ID
          ? {
              id: packageProtection.coverageMlModelId,
              config: {
                minPriceBiasPercentOfConstraintRange: packageProtection
                  .intentModelConfig.minPriceBiasPercentOfConstraintRange
                  ? parseFloat(
                      packageProtection.intentModelConfig
                        .minPriceBiasPercentOfConstraintRange,
                    )
                  : undefined,
                maxPriceBiasPercentOfConstraintRange: packageProtection
                  .intentModelConfig.maxPriceBiasPercentOfConstraintRange
                  ? parseFloat(
                      packageProtection.intentModelConfig
                        .maxPriceBiasPercentOfConstraintRange,
                    )
                  : undefined,
              },
            }
          : packageProtection.coverageMlModelId ===
              STAGECOACH_COVERAGE_ML_MODEL_ID
            ? {
                id: packageProtection.coverageMlModelId,
                config: {
                  priceVsConversionWeight:
                    packageProtection.stagecoachPriceVsConversionWeight
                      ? parseFloat(
                          packageProtection.stagecoachPriceVsConversionWeight,
                        )
                      : undefined,
                  thompsonSamplingEnabled:
                    packageProtection.stagecoachThompsonSamplingEnabled,
                  inferenceMode: packageProtection.stagecoachInferenceMode,
                },
              }
            : { id: packageProtection.coverageMlModelId }
        : undefined,
  };
}
